import { DOCUMENT } from '@angular/common';
import { Component, OnInit, Inject, inject, ElementRef } from '@angular/core';
import { Observable, fromEvent, asyncScheduler, from, skipWhile } from 'rxjs';
import { throttleTime, mergeMap, takeUntil, distinctUntilKeyChanged, switchMap, first } from 'rxjs/operators';

import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreLanguage } from 'idt-ecommerce-api/stores/stores.models';
import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderStateService } from 'render/services/render-state/render-state.service';
import { RENDER_STYLE_HOST } from 'render/tokens/render-style-host.token';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { AddFontFaceRulesToDOM } from 'shared/utils/fonts';

import { RenderConfigurationService } from './services/render-configuration.service';


@Component({
  selector: 'render-page',
  templateUrl: 'render-page.component.html'
})
export class RenderPageComponent extends UnsubscribeComponent implements OnInit {

  public layout$!: Observable<IdtComponent | undefined>;

  private readonly renderStyleHost = inject(RENDER_STYLE_HOST);

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly contextService: ContextService,
    private readonly renderSlugService: RenderSlugService,
    private readonly renderStateService: RenderStateService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly element: ElementRef
  ) {
    super();
    this.renderStyleHost.classHost.classList.add('mouse-navigate');
    const events: string[] = ['mousedown', 'keydown'];
    from(events)
      .pipe(
        mergeMap((event) => fromEvent(window, event)),
        throttleTime(500, asyncScheduler, { leading: false, trailing: true }),
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe((event) => {
        this.getNavigate(event);
      });
  }

  public ngOnInit(): void {
    const context$ = this.contextService.getContext$().pipe(
      skipWhile((context) => Object.keys(context.store).length === 0),
      first()
    );

    this.layout$ = context$.pipe(
      switchMap(() => this.renderStateService.getLayout$(this.renderSlugService.getSlug()))
    );

    context$.subscribe((context) => {
      this.addFonts(context.store.countryCode);
      if (context.config.brandSettings.setCookies && context.config.instanceType === 'render') {
        context.config.brandSettings.setCookies(this.document);
      }
    });

    this.contextService.getCurrentLanguage$().pipe(
      distinctUntilKeyChanged('direction'),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((currentLang: StoreLanguage) => {
      (this.element.nativeElement as HTMLElement).dir = currentLang.direction;
    });
  }

  public getNavigate(event: Event): void {
    if (event.type === 'mousedown') {
      this.renderStyleHost.classHost.classList.add('mouse-navigate');
    }
    if (event.type === 'keydown') {
      const eventKeyboard = event as KeyboardEvent;
      if (eventKeyboard.key === 'Tab') {
        this.renderStyleHost.classHost.classList.remove('mouse-navigate');
      }
    }
  }

  private addFonts(countryCode: string): void {
    if (this.renderConfigurationService.config.fonts) {
      const domain = this.renderConfigurationService.config.fonts.domains[countryCode]
        ?? this.renderConfigurationService.config.fonts.domains.default;
      AddFontFaceRulesToDOM(
        this.renderStyleHost.styleRoot,
        domain,
        this.renderConfigurationService.config.fonts.sources
      );
    }
  }

}
